import { useEffect, useState } from "react";
import { documentTaxLabel, type DocumentTaxCalculation } from "../lib/document-tax";
import { apiFetch } from "../lib/api-client";

type Totals = {
  subtotalCents?: number;
  discountCents?: number;
  discountPresentation?: {
    amountCents: number;
    treatment: "subtract_from_subtotal" | "included_in_line_items";
  } | null;
  taxCents: number;
  taxCalculation?: DocumentTaxCalculation | null;
};
const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

/** Read the saved document amounts; never infer tax from the balance or line sum. */
export function DocumentTaxSummary({ kind, id, totalCents, totalsOverride }: { kind: "invoices" | "estimates"; id: string; totalCents: number; totalsOverride?: Totals }) {
  const [result, setResult] = useState<{ key: string; totals: Totals | null } | null>(null);
  const key = `${kind}/${id}/${totalCents}`;
  useEffect(() => {
    if (totalsOverride) return;
    const controller = new AbortController();
    void apiFetch(`/api/v1/workspace/${kind}/${encodeURIComponent(id)}/document`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Document unavailable");
        const body = await response.json();
        const document = body?.data?.document;
        if (typeof document?.taxCents !== "number" || !Number.isFinite(document.taxCents)) throw new Error("Tax unavailable");
        if (!controller.signal.aborted) setResult({ key, totals: document });
      })
      .catch(() => { if (!controller.signal.aborted) setResult({ key, totals: null }); });
    return () => controller.abort();
  }, [kind, id, totalCents, key, totalsOverride]);
  const totals = totalsOverride ?? (result?.key === key ? result.totals : undefined);
  const discount = totals?.discountPresentation ??
    (totals?.discountCents
      ? {
          amountCents: Math.abs(totals.discountCents),
          treatment: totals.discountCents < 0 ? "included_in_line_items" as const : "subtract_from_subtotal" as const,
        }
      : null);
  return <dl className="mt-3 space-y-1 text-sm" aria-label="Document tax breakdown">
    {typeof totals?.subtotalCents === "number" && <div className="flex justify-between gap-3"><dt>Subtotal</dt><dd>{money(totals.subtotalCents)}</dd></div>}
    {discount && <div className="flex justify-between gap-3"><dt>{discount.treatment === "included_in_line_items" ? "Discounts included in line items" : "Discount"}</dt><dd>{discount.treatment === "subtract_from_subtotal" ? "−" : ""}{money(discount.amountCents)}</dd></div>}
    {totals?.taxCalculation?.status === "failed" && <div role="alert" className="text-[var(--st-conflict)]">Tax needs attention: {totals.taxCalculation.failureReason || "Tax could not be calculated."}</div>}
    <div className="flex justify-between gap-3"><dt>{documentTaxLabel(totals?.taxCalculation)}</dt><dd>{totals === undefined ? "Loading…" : totals === null ? "Unavailable" : money(totals.taxCents)}</dd></div>
  </dl>;
}
